import React, { ReactNode } from 'react';
import Grid from '@mui/material/Grid';
import { colors } from 'theme/colors';
import { Typography } from '@mui/material';
import Button, { IButton } from 'components/atoms/Button';
import { EButtonSizes, EButtonSizesPX, EButtonVariants } from 'constants/Buttons';
import { EIconTypes } from 'constants/Icons';
import { useThemeBreakpoint } from 'hooks/useThemeBreakpoint';
import { useTranslations } from 'hooks/useTranslations';
import { ITranslationKey } from 'components/molecules/Table';
import { EPermission } from 'models/permissions';
import { Link } from 'react-router-dom';

export enum EDetailsPageTypes {
  view = 'view',
  delete = 'delete',
  userView = 'user_view',
  auditLogView = 'audit_log_view',
  transactionSelectView = 'transaction_select_view',
  apiKeyView = 'api_key_view',
  frtView = 'frt_view',
}

interface IactionButton {
  text?: string;
  icon?: EIconTypes;
  sizePX?: EButtonSizesPX;
  onClick?: () => void;
  variant?: EButtonVariants;
  toNewTab?: string;
}

export interface IDetailsTemplateProps {
  children: ReactNode;
  title: string | undefined;
  customTitleContent?: ReactNode;
  pageType?: EDetailsPageTypes;
  redirectToEditView?: (a: string) => void;
  redirectToDisableView?: (a: string) => void;
  redirectToGeneratePasswordView?: (a: string) => void;
  redirectToView?: (a: string) => void;
  redirectToSelect?: (a: string) => void;
  actionButtonProps?: IButton;
  actionButtonFullRow?: boolean;
  actionButtons?: IactionButton[];
  translationsKeys?: ITranslationKey | any;
  hasLongTitle?: boolean;
  disableActions?: boolean;
  changeActivationUserStatus?: () => void;
  unbanUserStatus?: () => void;
  userActivated?: boolean;
  userBlocked?: boolean;
  redirectToShowOnMap?: () => void;
  redirectToMovedTransaction?: () => void;
  hideMovedTransaction?: boolean;
  deletePermissions?: EPermission[];
  editPermissions?: EPermission[];
  disableOnlyDelete?: boolean;
  showOnMapButtonId?: string;
  loggedUserIdEqualsViewUserId?: boolean;
  handleDelete?: () => void;
  resetOfflineAuthenticator?: () => void;
  changeLocationButtonVisibility?: boolean;
  showEditButton?: boolean;
}

const DetailsTemplate = ({
  children,
  title,
  customTitleContent,
  showOnMapButtonId,
  pageType = EDetailsPageTypes.delete,
  redirectToEditView,
  editPermissions,
  redirectToDisableView,
  changeActivationUserStatus,
  unbanUserStatus,
  redirectToGeneratePasswordView,
  redirectToView,
  redirectToSelect,
  actionButtonProps,
  actionButtonFullRow = false,
  actionButtons,
  translationsKeys,
  userActivated,
  userBlocked,
  disableActions = false,
  deletePermissions,
  disableOnlyDelete = false,
  redirectToShowOnMap,
  redirectToMovedTransaction,
  hideMovedTransaction,
  handleDelete,
  resetOfflineAuthenticator,
  loggedUserIdEqualsViewUserId,
  changeLocationButtonVisibility = true,
  showEditButton = true,
}: IDetailsTemplateProps) => {
  const { isDesktop } = useThemeBreakpoint();
  const { t } = useTranslations();

  const renderLegalEntityDetailsActionButtons = (isTemplateFooter: boolean) => {
    if (
      pageType === EDetailsPageTypes.view ||
      pageType === EDetailsPageTypes.userView ||
      pageType === EDetailsPageTypes.auditLogView ||
      pageType === EDetailsPageTypes.transactionSelectView
    ) {
      let buttonProps: IButton | undefined;
      if (actionButtonProps) {
        buttonProps = actionButtonProps;
      } else {
        buttonProps = {
          ...(isDesktop
            ? { icon: EIconTypes.delete, sizePX: EButtonSizesPX.px32 }
            : { label: t(translationsKeys?.deleteLink || 'general.delete.text') }),
          ...(isDesktop ? {} : { size: EButtonSizes.small }),
          ...(isDesktop ? {} : { sizePX: EButtonSizesPX.px24 }),
          ...(isDesktop
            ? { variant: EButtonVariants.outlined }
            : { variant: EButtonVariants.outlined }),
        };
      }
      return (
        <Grid
          container
          size={{ xs: !isTemplateFooter ? 12 : 'auto' }}
          sx={{
            display: isTemplateFooter && !isDesktop ? 'none' : 'flex',
            justifyContent: isTemplateFooter ? 'flex-end' : 'space-between',
            alignItems: 'center',
            borderTop: {
              xs: `1px solid ${colors.primary100}`,
              lg: 'none',
            },
            mb: pageType === EDetailsPageTypes.userView ? { lg: 1, xl: 0 } : null,
            pt: {
              xs: isTemplateFooter ? 0 : 2,
              lg: 0,
            },
            mt: {
              xs: isTemplateFooter ? 0 : 2,
              lg: 0,
            },
            flexWrap: { xs: 'wrap', md: 'nowrap' },
          }}
        >
          {!isDesktop && !isTemplateFooter && (
            <Grid
              size={{ xs: 12, sm: actionButtonFullRow ? 12 : 'auto' }}
              sx={{ mb: { xs: isTemplateFooter ? 0 : 2, sm: actionButtonFullRow ? 2 : 0 } }}
            >
              <Typography variant="button" sx={{ textTransform: 'uppercase' }}>
                {t('general.actions.text')}:
              </Typography>
            </Grid>
          )}
          <Grid
            container
            size={{ xs: 12, sm: actionButtonFullRow ? 12 : 'auto' }}
            spacing={1}
            sx={{
              display: {
                xs: isTemplateFooter ? 'none' : 'flex',
                lg: isTemplateFooter ? 'flex' : 'none',
              },
              justifyContent:
                pageType === EDetailsPageTypes.userView
                  ? {
                      xs: 'flex-end',
                      md: 'flex-start',
                      xl: 'flex-end',
                    }
                  : 'flex-end',
              alignItems: 'center',
              flexWrap: { xs: 'wrap', xl: 'nowrap' },
            }}
          >
            {redirectToShowOnMap && pageType !== EDetailsPageTypes.auditLogView && (
              <Grid
                size={{
                  xs: 12,
                  sm: isTemplateFooter || !actionButtonFullRow ? 'auto' : 12,
                }}
              >
                <Button
                  id={showOnMapButtonId}
                  label={t('registeredFirearms.firearmsDetails.showOnMap.button')}
                  variant={EButtonVariants.contained}
                  onClick={redirectToShowOnMap}
                  size={EButtonSizes.small}
                  fullWidth
                />
              </Grid>
            )}
            {redirectToMovedTransaction &&
              !hideMovedTransaction &&
              pageType !== EDetailsPageTypes.auditLogView &&
              changeLocationButtonVisibility && (
                <Grid
                  size={{
                    xs: 12,
                    sm: isTemplateFooter || !actionButtonFullRow ? 'auto' : 12,
                  }}
                >
                  <Button
                    id="move-transaction-button"
                    label={t(
                      'registeredFirearms.firearmsDetails.redirectToMovedTransaction.button',
                    )}
                    variant={EButtonVariants.contained}
                    onClick={redirectToMovedTransaction}
                    size={EButtonSizes.small}
                    fullWidth
                  />
                </Grid>
              )}
            {redirectToEditView &&
              ![
                EDetailsPageTypes.transactionSelectView,
                EDetailsPageTypes.auditLogView,
              ].includes(pageType) &&
              !!redirectToEditView && (
                <Grid
                  container
                  size={{ xs: 12, sm: 'auto' }}
                  sx={{
                    flexWrap: {
                      xs: pageType === EDetailsPageTypes.userView ? 'wrap' : 'nowrap',
                      sm: 'nowrap',
                    },
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 1,
                  }}
                >
                  {showEditButton && (
                    <Grid size={{ xs: isTemplateFooter ? 'auto' : 12 }}>
                      <Button
                        id="edit"
                        label={t('userDetails.edit.link')}
                        variant={EButtonVariants.outlined}
                        onClick={redirectToEditView}
                        permission={editPermissions}
                        size={EButtonSizes.small}
                        fullWidth
                      />
                    </Grid>
                  )}
                  {userBlocked && unbanUserStatus && !loggedUserIdEqualsViewUserId && (
                    <Grid size={{ xs: isTemplateFooter ? 'auto' : 12 }}>
                      <Button
                        label={t('userDetails.unlock.link')}
                        id="activate"
                        variant={EButtonVariants.outlined}
                        onClick={unbanUserStatus}
                        size={EButtonSizes.small}
                        fullWidth
                      />
                    </Grid>
                  )}
                  {handleDelete && (
                    <Grid size={{ xs: isTemplateFooter ? 'auto' : 12 }}>
                      <Button
                        label={t('general.delete.text')}
                        id="delete_button"
                        variant={EButtonVariants.outlined}
                        onClick={handleDelete}
                        size={EButtonSizes.small}
                        sx={{
                          width: { xs: '100%' },
                          minWdth: { xs: '109px', lg: 'auto' },
                        }}
                      />
                    </Grid>
                  )}
                  {changeActivationUserStatus &&
                    !userBlocked &&
                    !loggedUserIdEqualsViewUserId && (
                      <Grid size={{ xs: isTemplateFooter ? 'auto' : 12 }}>
                        <Button
                          label={
                            userActivated
                              ? t('userDetails.deactivate.link')
                              : t('userDetails.activate.link')
                          }
                          id={userActivated ? 'deactivate' : 'activate'}
                          variant={EButtonVariants.outlined}
                          onClick={changeActivationUserStatus}
                          size={EButtonSizes.small}
                          fullWidth
                        />
                      </Grid>
                    )}
                </Grid>
              )}
            {redirectToGeneratePasswordView && (
              <Grid size={{ xs: isTemplateFooter ? 'auto' : 12 }}>
                <Button
                  label={t('userDetails.generatePassword.link')}
                  id="generatePassword"
                  variant={EButtonVariants.outlined}
                  onClick={redirectToGeneratePasswordView}
                  size={EButtonSizes.small}
                  fullWidth
                />
              </Grid>
            )}
            {redirectToSelect && pageType === EDetailsPageTypes.transactionSelectView && (
              <Grid container spacing={2} sx={{ display: 'flex', flexDirection: 'row' }}>
                <Button
                  label={t('registeredFirearms.firearmsDetails.select.button')}
                  id="select"
                  variant={EButtonVariants.outlined}
                  onClick={redirectToSelect}
                  size={EButtonSizes.small}
                  fullWidth
                />
              </Grid>
            )}

            {actionButtons &&
              actionButtons.length > 0 &&
              actionButtons.map((button) => {
                if (!button.text && !button.icon) return null;
                return (
                  <Grid
                    container
                    key={button.text}
                    size={{ xs: 12, sm: 'auto' }}
                    spacing={2}
                    sx={{ display: 'flex', flexDirection: 'row' }}
                  >
                    {button.toNewTab ? (
                      <Link
                        to={button.toNewTab}
                        target="_blank"
                        style={{ width: '100%', display: 'block' }}
                      >
                        <Button
                          label={button.text ? t(button.text) : undefined}
                          icon={button.icon}
                          variant={button.variant || EButtonVariants.outlined}
                          size={EButtonSizes.small}
                          fullWidth
                          sizePX={button.sizePX}
                        />
                      </Link>
                    ) : (
                      <Button
                        label={button.text ? t(button.text) : undefined}
                        icon={button.icon}
                        variant={button.variant || EButtonVariants.outlined}
                        onClick={button.onClick || (() => {})}
                        size={EButtonSizes.small}
                        fullWidth
                        sizePX={button.sizePX}
                      />
                    )}
                  </Grid>
                );
              })}

            {pageType === EDetailsPageTypes.view && redirectToDisableView && (
              <Grid>
                <Button
                  icon={EIconTypes.shutDown}
                  id="disable"
                  variant={EButtonVariants.outlined}
                  onClick={redirectToDisableView}
                  sizePX={EButtonSizesPX.px32}
                />
              </Grid>
            )}

            {pageType === EDetailsPageTypes.view && !disableOnlyDelete && !!redirectToView && (
              <Grid size={{ xs: isTemplateFooter ? 'auto' : 12 }}>
                <Button
                  {...buttonProps}
                  sx={buttonProps ? { ...buttonProps.sx } : {}}
                  permission={deletePermissions}
                  onClick={redirectToView}
                  id="delete"
                  fullWidth
                />
              </Grid>
            )}

            {pageType === EDetailsPageTypes.auditLogView && (
              <Grid size={{ xs: isTemplateFooter ? 'auto' : 12 }}>
                <Button
                  label={t('roles.rolesTable.view.link')}
                  id="view"
                  variant={EButtonVariants.outlined}
                  onClick={redirectToView}
                  size={EButtonSizes.small}
                  fullWidth
                />
              </Grid>
            )}

            {resetOfflineAuthenticator && (
              <Grid size={{ xs: isTemplateFooter ? 'auto' : 12 }}>
                <Button
                  label={t('userDetails.resetOfflineAuthenticator.link')}
                  id="generatePassword"
                  variant={EButtonVariants.outlined}
                  onClick={resetOfflineAuthenticator}
                  size={EButtonSizes.small}
                  fullWidth
                />
              </Grid>
            )}
          </Grid>
        </Grid>
      );
    }
    return null;
  };

  return (
    <Grid
      container
      sx={{
        border: `1px solid ${colors.primary100}`,
        borderRadius: '4px',
        overflow: 'hidden',
      }}
    >
      <Grid
        size={{ xs: 12 }}
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          backgroundColor: colors.primary100,
          flexDirection:
            pageType === EDetailsPageTypes.userView
              ? {
                  md: 'column',
                  lg: 'column',
                  xl: 'row',
                }
              : 'row',
          px: 2.5,
          py: 1,
          gap: 3,
        }}
      >
        {customTitleContent || (
          <>
            <Grid
              sx={{
                mb: pageType === EDetailsPageTypes.userView ? { lg: 1, xl: 0 } : null,
                display: 'contents',
              }}
            >
              <Typography variant="h5" sx={{ fontWeight: 600, lineHeight: '2rem' }}>
                {title}
              </Typography>
            </Grid>
            {!disableActions && renderLegalEntityDetailsActionButtons(true)}
          </>
        )}
      </Grid>
      <Grid container size={{ xs: 12 }} spacing={2} sx={{ p: 2.5 }}>
        {children}
        {!disableActions && renderLegalEntityDetailsActionButtons(false)}
      </Grid>
    </Grid>
  );
};

export { DetailsTemplate };
